import { bungie, BungieError } from '../lib/bungie';
import { definition, items, table, tablesOf } from '../lib/manifest';
import type { ItemDef, SlimItem, Table } from '../lib/tables';
import type { Player } from './hub';

/** Port of the app's Guardian (worn gear), Stash (inventory, vault), Vendors, Loadouts and QuestLog. */

export const SUBCLASS_BUCKET = 3284755031;
export const WEAPON_BUCKETS = [1498876634, 2465295065, 953998645];
export const ARMOR_BUCKETS = [3448274439, 3551918588, 14239492, 20886954, 1585787867];
export const SLOTS: { bucket: number; title: string }[] = [
  { bucket: SUBCLASS_BUCKET, title: 'Subclass' }, { bucket: 1498876634, title: 'Kinetic Weapons' }, { bucket: 2465295065, title: 'Energy Weapons' },
  { bucket: 953998645, title: 'Power Weapons' }, { bucket: 3448274439, title: 'Helmet' }, { bucket: 3551918588, title: 'Gauntlets' },
  { bucket: 14239492, title: 'Chest Armor' }, { bucket: 20886954, title: 'Leg Armor' }, { bucket: 1585787867, title: 'Class Armor' },
  { bucket: 4023194814, title: 'Ghost' }, { bucket: 2025709351, title: 'Vehicle' }, { bucket: 284967655, title: 'Ships' }, { bucket: 4274335291, title: 'Emblems' },
];

// ---------------- what each character is wearing ----------------

export interface SubclassPlug { kind: string; name: string; type: string; desc: string; icon: string }
export interface WornItem {
  bucket: number; hash: number; name: string; type: string; tier: number; flavor: string; icon: string; power?: number;
  shaderName: string; ornamentName: string; masterwork: boolean; plugs: SubclassPlug[];
  shader?: number; ornament?: number;   // the plug hashes, for the 3D view
  watermark: string; shot: string; gearTier: number;   // the season mark, Bungie's picture and the gear tier (the 3D view's squares and cards)
}
export interface GearStat { name: string; icon: string; value: number }
export interface GearChar { id: string; cls: string; title: string; stats: GearStat[]; items: WornItem[] }

export async function loadGear(player: Player, token?: string): Promise<GearChar[]> {
  const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/?components=200,205,300,305`, { token });
  const [lite, t] = await Promise.all([items(), tablesOf('DestinyStatDefinition', 'DestinyRecordDefinition')]);
  const chars: GearChar[] = [];
  for (const [cid, d] of Object.entries<any>(p.characters?.data ?? {})) {
    const worn: WornItem[] = [];
    for (const it of p.characterEquipment?.data?.[cid]?.items ?? []) {
      const def = lite[it.itemHash];
      if (!def) continue;
      const iid = it.itemInstanceId;
      const inst = iid ? p.itemComponents?.instances?.data?.[iid] : undefined;
      const sockets: any[] = iid ? p.itemComponents?.sockets?.data?.[iid]?.sockets ?? [] : [];
      const plugs: SubclassPlug[] = [];
      let shaderName = '', ornamentName = '', shader: number | undefined, ornament: number | undefined;
      if (it.bucketHash === SUBCLASS_BUCKET)
        for (const sk of sockets) {
          const pd = sk.plugHash ? lite[sk.plugHash] : undefined;
          if (!pd?.name || pd.name.includes('Empty')) continue;
          const kind = /Super/.test(pd.type) ? 'Super' : /Aspect/.test(pd.type) ? 'Aspect' : /Fragment/.test(pd.type) ? 'Fragment'
            : /Grenade|Melee|Class Ability|Movement|Jump|Ability/.test(pd.type) ? 'Ability' : '';
          if (kind) plugs.push({ kind, name: pd.name, type: pd.type, desc: pd.desc, icon: pd.icon });
        }
      if (ARMOR_BUCKETS.includes(it.bucketHash))
        for (const sk of sockets) {
          const pd = sk.plugHash ? lite[sk.plugHash] : undefined;
          if (!pd) continue;
          if (pd.type.includes('Shader') && !shaderName) { shaderName = pd.name; shader = sk.plugHash; }
          if (pd.type.includes('Ornament') && !pd.name.startsWith('Default') && !ornamentName) { ornamentName = pd.name; ornament = sk.plugHash; }
        }
      worn.push({
        bucket: it.bucketHash, hash: it.itemHash, name: def.name, type: def.type, tier: def.tier, flavor: '', icon: def.icon, watermark: def.watermark, shot: '', gearTier: inst?.gearTier ?? 0,
        power: inst?.primaryStat?.value, shaderName, ornamentName, shader, ornament, masterwork: ((it.state ?? 0) & 4) !== 0, plugs,
      });
    }
    // the flavour text and picture are only in the full item definitions: one small request per worn item, kept
    await Promise.all(worn.map(async (w) => { const full = await definition<any>('DestinyInventoryItemDefinition', w.hash); w.flavor = full?.flavorText ?? ''; w.shot = full?.screenshot ?? ''; }));
    const stats: GearStat[] = [];
    for (const [sh, v] of Object.entries<any>(d.stats ?? {})) {
      if (sh === '1935470627') continue;   // Power is shown separately
      const sd = t.DestinyStatDefinition[sh]?.displayProperties;
      if (sd?.name) stats.push({ name: sd.name, icon: sd.icon, value: Number(v) });
    }
    // Bungie sends them in the game's order, but number-like keys come out sorted: put the game's order back
    const order = ['Health', 'Melee', 'Grenade', 'Super', 'Class', 'Weapons'];
    const at = (n: string) => (order.indexOf(n) < 0 ? 99 : order.indexOf(n));
    stats.sort((a, b) => at(a.name) - at(b.name));
    const td = d.titleRecordHash ? t.DestinyRecordDefinition[d.titleRecordHash] : undefined;
    const title = td?.titleInfo?.titlesByGender?.[d.genderType === 1 ? 'Female' : 'Male'] ?? '';
    chars.push({ id: cid, cls: ['Titan', 'Hunter', 'Warlock'][d.classType] ?? '', title, stats, items: worn });
  }
  return chars;
}

/**
 * One item as 3D View draws it worn, read on its own (its Power, shader and ornament): right after an equip, so the
 * squares and the Guardian change at once instead of waiting for Bungie's profile to catch up (it can take half a
 * minute).
 */
export async function wornFromItem(player: Player, token: string, i: { hash: number; instanceId: string }, bucket: number): Promise<WornItem> {
  const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/Item/${i.instanceId}/?components=300,305,307`, { token });
  const [lite, full] = await Promise.all([items(), definition<any>('DestinyInventoryItemDefinition', i.hash)]);
  const def = lite[i.hash];
  let shaderName = '', ornamentName = '', shader: number | undefined, ornament: number | undefined;
  if (ARMOR_BUCKETS.includes(bucket))
    for (const sk of p.sockets?.data?.sockets ?? []) {
      const pd = sk.plugHash ? lite[sk.plugHash] : undefined;
      if (!pd) continue;
      if (pd.type.includes('Shader') && !shaderName) { shaderName = pd.name; shader = sk.plugHash; }
      if (pd.type.includes('Ornament') && !pd.name.startsWith('Default') && !ornamentName) { ornamentName = pd.name; ornament = sk.plugHash; }
    }
  const inst = p.instance?.data;
  return {
    bucket, hash: i.hash, name: def?.name ?? '', type: def?.type ?? '', tier: def?.tier ?? 0, flavor: full?.flavorText ?? '', icon: def?.icon ?? '',
    watermark: def?.watermark ?? '', shot: full?.screenshot ?? '', gearTier: inst?.gearTier ?? 0, power: inst?.primaryStat?.value,
    shaderName, ornamentName, shader, ornament, masterwork: ((p.item?.data?.state ?? 0) & 4) !== 0, plugs: [],
  };
}

// ---------------- everything held that is not worn (private) ----------------

export const VAULT = 138197802, LOST_ITEMS = 215593132, ENGRAMS = 375726501, CONSUMABLES = 1469714392, MODIFICATIONS = 3313201758, MATERIALS = 2207872501;

export interface StashItem {
  hash: number; name: string; icon: string; typeName: string; itemType: number; tier: number; damage: number; ammo: number; classType: number;
  quantity: number; power?: number; state: number; bucket: number; instanceId: string; watermark: string;
  slot: number;   // the bucket it is worn in (an item in the vault has the vault's bucket)
  gearTier?: number;   // the gear tier (1-5), drawn as diamonds down the square's left edge as DIM does
  iconHash?: number;   // its layered picture (DestinyIconDefinition), as DIM draws its squares
}
export const isWeapon = (i: StashItem) => i.itemType === 3;
export const isArmor = (i: StashItem) => i.itemType === 2;
export const isLocked = (i: StashItem) => (i.state & 1) !== 0;
export const isMasterwork = (i: StashItem) => (i.state & 4) !== 0;
export const isCrafted = (i: StashItem) => (i.state & 8) !== 0;

export interface Currency { name: string; icon: string; quantity: number }
export interface Stash {
  account: StashItem[];
  characters: Record<string, StashItem[]>;
  /** Each character's class stats (Health, Melee...), in the game's order. */
  stats: Record<string, GearStat[]>;
  /** What each character is wearing (the Item Manager shows it beside what they carry). */
  equipped: Record<string, StashItem[]>;
  currencies: Currency[];
  buckets: Record<number, { name: string; capacity: number }>;
}

export const inBucket = (s: Stash, bucket: number, charId?: string) => (charId ? s.characters[charId] ?? [] : s.account).filter((i) => i.bucket === bucket);
export const capacity = (s: Stash, bucket: number, fallback: number) => s.buckets[bucket]?.capacity || fallback;

export async function loadStash(player: Player, token: string): Promise<Stash> {
  const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/?components=102,103,200,201,205,300`, { token });
  const account: any[] | undefined = p.profileInventory?.data?.items;
  if (!account) throw new BungieError(0, 'Private', "Bungie did not include this account's inventory. Sign in with the Bungie account that owns it.");
  const [lite, bucketDefs, statDefs] = await Promise.all([items(), table<any>('DestinyInventoryBucketDefinition'), table<any>('DestinyStatDefinition')]);
  const instances = p.itemComponents?.instances?.data ?? {};
  const item = (i: any): StashItem | null => {
    const d: ItemDef | undefined = lite[i.itemHash];
    if (!d?.name) return null;
    const inst = i.itemInstanceId ? instances[i.itemInstanceId] : undefined;
    return {
      hash: i.itemHash, name: d.name, icon: d.icon, typeName: d.type, itemType: d.itemType, tier: d.tier,
      damage: inst?.damageType || d.damage, ammo: d.ammo, classType: d.classType, quantity: Math.max(1, i.quantity ?? 1),
      power: inst?.primaryStat?.value, state: i.state ?? 0, bucket: i.bucketHash, instanceId: i.itemInstanceId ?? '', watermark: d.watermark, slot: d.bucket,
      gearTier: inst?.gearTier || undefined,
      iconHash: d.iconHash,
    };
  };
  const characters: Record<string, StashItem[]> = {};
  const allBuckets = new Set<number>(account.map((i) => i.bucketHash));
  for (const [cid, c] of Object.entries<any>(p.characterInventories?.data ?? {})) {
    characters[cid] = (c.items ?? []).map(item).filter(Boolean);
    for (const i of c.items ?? []) allBuckets.add(i.bucketHash);
  }
  const equipped: Record<string, StashItem[]> = {};
  for (const [cid, c] of Object.entries<any>(p.characterEquipment?.data ?? {})) {
    equipped[cid] = (c.items ?? []).map(item).filter(Boolean);
    for (const i of c.items ?? []) allBuckets.add(i.bucketHash);
  }
  const buckets: Stash['buckets'] = {};
  for (const b of allBuckets) {
    const d = bucketDefs[b];
    if (d) buckets[b] = { name: d.displayProperties?.name ?? '', capacity: d.itemCount ?? 0 };
  }
  const currencies = (p.profileCurrencies?.data?.items ?? [])
    .map((m: any) => ({ d: lite[m.itemHash], q: m.quantity ?? 0 })).filter((m: any) => m.d?.name)
    .map((m: any) => ({ name: m.d.name, icon: m.d.icon, quantity: m.q }));
  const stats: Record<string, GearStat[]> = {};
  const order = ['Health', 'Melee', 'Grenade', 'Super', 'Class', 'Weapons'];
  for (const [cid, c] of Object.entries<any>(p.characters?.data ?? {})) {
    stats[cid] = Object.entries<any>(c.stats ?? {}).filter(([sh]) => sh !== '1935470627')
      .map(([sh, v]) => ({ name: statDefs[sh]?.displayProperties?.name ?? '', icon: statDefs[sh]?.displayProperties?.icon ?? '', value: Number(v) }))
      .filter((s) => s.name).sort((a, b) => (order.indexOf(a.name) + 1 || 99) - (order.indexOf(b.name) + 1 || 99));
  }
  return { account: account.map(item).filter(Boolean) as StashItem[], characters, equipped, stats, currencies, buckets };
}

// ---------------- moving and equipping (needs the "move or equip" permission) ----------------

export async function pullFromPostmaster(player: Player, token: string, characterId: string, i: StashItem) {
  await bungie('/Destiny2/Actions/Items/PullFromPostmaster/', {
    token, method: 'POST',
    body: { itemReferenceHash: i.hash, stackSize: i.quantity, itemId: i.instanceId || '0', characterId, membershipType: player.membershipType },
  });
}

export async function equipItem(player: Player, token: string, characterId: string, i: StashItem) {
  if (!i.instanceId) throw new BungieError(0, 'NotEquippable', 'This item cannot be equipped.');
  await bungie('/Destiny2/Actions/Items/EquipItem/', { token, method: 'POST', body: { itemId: i.instanceId, characterId, membershipType: player.membershipType } });
}

// ---------------- one item's own roll (the Item Manager's card) ----------------

export interface PlugStat { name: string; value: number }
export interface RolledPlug { hash: number; name: string; icon: string; desc: string; type: string; enhanced?: boolean; stats?: PlugStat[] }
/** A stat, and what of it comes from the perks, the mods and the masterwork (DIM's coloured bar); minus: what they take away. */
export interface RolledStat { name: string; value: number; bar: boolean; perks?: number; mods?: number; mw?: number; minus?: number; masterworked?: boolean }
export interface PerkColumn { selected: number; options: RolledPlug[] }
/** A count kept on the item: its kill tracker, or a catalyst's goal. */
export interface RolledObjective { name: string; progress: number; total: number; tracker: boolean; done: boolean }
export interface Rolled { stats: RolledStat[]; intrinsic?: RolledPlug; perks: RolledPlug[]; columns: PerkColumn[]; mods: RolledPlug[]; objectives: RolledObjective[] }

// the game's order of the stats (as the in-game inspect screen and DIM show them); the rest after
const STAT_ORDER = ['Rounds Per Minute', 'Draw Time', 'Charge Time', 'Impact', 'Blast Radius', 'Velocity', 'Range', 'Stability', 'Handling', 'Reload Speed',
  'Aim Assistance', 'Airborne Effectiveness', 'Zoom', 'Recoil Direction', 'Ammo Generation', 'Magazine', 'Health', 'Melee', 'Grenade', 'Super', 'Class', 'Weapons'];
const NUMBER_STATS = /^(Rounds Per Minute|Draw Time|Charge Time|Magazine)$/;

/** One item's roll as Bungie sends it: stats, sockets, what each socket can switch to, the counts on its plugs. */
export interface RawRoll { stats: Record<string, any>; sockets: any[]; plugs: Record<string, any[]>; objectives: Record<string, any[]> }
/** Every item's roll from one profile read, as DIM reads them: the god-roll marks' perk lists come from it too. */
export interface Rolls { plugged: Record<string, number[]>; raw: Record<string, RawRoll> }
/** The tables a card is built from, all kept in the browser. */
export interface RollTables { lite: Table<ItemDef>; statDefs: Table<any>; slim: Table<SlimItem>; objectives: Table<any> }

/** The tables a card needs (the slim item table is a one-off download per Destiny update, read in the background). */
export const rollTables = async (): Promise<RollTables> => {
  const [lite, statDefs, slim, objectives] = await Promise.all([items(), table<any>('DestinyStatDefinition'), table<SlimItem>('DestinyInventoryItemDefinition'), table<any>('DestinyObjectiveDefinition')]);
  return { lite, statDefs, slim, objectives };
};

/**
 * Every item's roll in one read (stats, sockets, switchable perks, plug counts), so a card opens at once as DIM's do.
 * Heavier than the stash, so the Item Manager asks for it after the board is up.
 */
export async function loadRolls(player: Player, token: string): Promise<Rolls> {
  const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/?components=102,201,205,304,305,309,310`, { token });
  const c = p.itemComponents ?? {};
  const raw: Record<string, RawRoll> = {};
  const plugged: Record<string, number[]> = {};
  for (const [iid, s] of Object.entries<any>(c.sockets?.data ?? {})) {
    raw[iid] = { stats: c.stats?.data?.[iid]?.stats ?? {}, sockets: s.sockets ?? [], plugs: c.reusablePlugs?.data?.[iid]?.plugs ?? {}, objectives: c.plugObjectives?.data?.[iid]?.objectivesPerPlug ?? {} };
    plugged[iid] = (s.sockets ?? []).map((k: any) => k.plugHash).filter(Boolean);
  }
  // the perks each column can be switched to (two-perk columns, crafted weapons): a roll counts if they are there
  for (const [iid, r] of Object.entries<any>(c.reusablePlugs?.data ?? {}))
    for (const list of Object.values<any>(r.plugs ?? {})) for (const pl of list ?? []) if (pl.plugItemHash) (plugged[iid] ??= []).push(pl.plugItemHash);
  return { plugged, raw };
}

/** One item's roll, read on its own (when a card opens before every roll has arrived). */
export async function loadRolled(player: Player, token: string, i: StashItem): Promise<Rolled> {
  const [p, t] = await Promise.all([
    bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/Item/${i.instanceId}/?components=304,305,309,310`, { token }),
    rollTables(),
  ]);
  return buildRolled(i, { stats: p.stats?.data?.stats ?? {}, sockets: p.sockets?.data?.sockets ?? [], plugs: p.reusablePlugs?.data?.plugs ?? {}, objectives: p.plugObjectives?.data?.objectivesPerPlug ?? {} }, t);
}

/** What this one copy rolled: its stats (with what perks, mods and masterwork add), frame, perk columns, mods and counts. */
export function buildRolled(i: StashItem, r: RawRoll, { lite, statDefs, slim, objectives: objectiveDefs }: RollTables): Rolled {
  const stats: RolledStat[] = [];
  for (const [sh, s] of Object.entries<any>(r.stats)) {
    const name = statDefs[sh]?.displayProperties?.name ?? '';
    if (name && name !== 'Power' && name !== 'Attack' && name !== 'Defense') stats.push({ name, value: s.value ?? 0, bar: !NUMBER_STATS.test(name) });
  }
  const rank = (n: string) => (STAT_ORDER.indexOf(n) < 0 ? 99 : STAT_ORDER.indexOf(n));
  stats.sort((a, b) => rank(a.name) - rank(b.name));
  const live = r.sockets;
  const plugOf = (hash: number): RolledPlug | undefined => {
    const d = lite[hash];
    if (!d?.name || /^(Empty|Default)/.test(d.name) || /Tracker$/.test(d.name) || /Shader|Ornament|Tracker|Memento/.test(d.type)) return undefined;
    return {
      hash, name: d.name, icon: d.icon, desc: d.desc, type: d.type, enhanced: /Enhanced/.test(d.type),
      stats: (slim[hash]?.stats ?? []).map(([sh, v]) => ({ name: statDefs[sh]?.displayProperties?.name ?? '', value: v })).filter((s) => s.name),
    };
  };
  const plugAt = (k: number) => (live[k]?.plugHash && live[k].isVisible !== false ? plugOf(live[k].plugHash) : undefined);
  const cats = slim[i.hash]?.cats ?? [];
  const at = (...hashes: number[]) => cats.filter(([c]) => hashes.includes(c)).flatMap(([, idx]) => idx);
  const pick = (idx: number[]) => idx.map(plugAt).filter((x): x is RolledPlug => !!x);
  const intrinsic = pick(at(3956125808))[0];
  const perks = pick(at(4241085061, 3154740035));   // weapon perks, armour perks
  // each perk column with every perk it can switch to (two-perk columns, crafted weapons), the plugged one first
  const columns = at(4241085061, 3154740035).map((k) => {
    const sel = plugAt(k);
    const opts = (r.plugs[k] ?? []).map((a: any) => plugOf(a.plugItemHash)).filter((x): x is RolledPlug => !!x && x.hash !== sel?.hash);
    return { selected: sel?.hash ?? 0, options: sel ? [sel, ...opts] : opts };
  }).filter((c) => c.options.length > 0);
  const mods = pick(at(2685412949, 590099826));     // weapon mods, armour mods (the masterwork among them)

  // the coloured parts of each bar: perks (the plugged ones), mods, masterwork; what any of them take away apart
  const isMw = (x: RolledPlug) => /Masterwork/.test(x.type) || /^Tier \d+:/.test(x.name);
  const plugged = [...columns.map((c) => c.options.find((o) => o.hash === c.selected)).filter((x): x is RolledPlug => !!x), ...(columns.length ? [] : perks)];
  for (const s of stats) {
    const sum = (list: RolledPlug[]) => list.reduce((n, x) => n + (x.stats ?? []).filter((t) => t.name === s.name).reduce((m, t) => m + t.value, 0), 0);
    const pos = (list: RolledPlug[]) => Math.max(0, sum(list));
    const neg = (list: RolledPlug[]) => Math.max(0, -sum(list));
    const mw = mods.filter(isMw), other = mods.filter((x) => !isMw(x));
    s.perks = pos(plugged); s.mods = pos(other); s.mw = pos(mw);
    s.minus = neg(plugged) + neg(other) + neg(mw);
    s.masterworked = s.mw > 0;
  }

  // the counts kept on its plugs: the kill tracker plugged, a catalyst's goal
  const objectives: RolledObjective[] = [];
  for (const sk of live) {
    const list = sk?.plugHash ? r.objectives[sk.plugHash] : undefined;
    if (!list?.length) continue;
    const pd = lite[sk.plugHash];
    const tracker = /Tracker/.test(pd?.type ?? '') || /Tracker$/.test(pd?.name ?? '');
    if (tracker && sk.isEnabled === false) continue;
    for (const o of list) {
      const od = objectiveDefs[o.objectiveHash];
      const name = od?.progressDescription || od?.displayProperties?.name || pd?.name || '';
      if (name) objectives.push({ name, progress: o.progress ?? 0, total: o.completionValue ?? 0, tracker, done: !!o.complete });
    }
  }
  return { stats, intrinsic, perks, columns, mods, objectives };
}

/** Locks or unlocks an item (Bungie wants a character id even for an item in the vault: any of the player's will do). */
export async function setLock(player: Player, token: string, characterId: string, i: StashItem, locked: boolean) {
  await bungie('/Destiny2/Actions/Items/SetLockState/', { token, method: 'POST', body: { state: locked, itemId: i.instanceId, characterId, membershipType: player.membershipType } });
}

/** Between the vault and a character, or character to character by way of the vault. from / to: a character id, or null for the vault. */
export async function moveItem(player: Player, token: string, i: StashItem, from: string | null, to: string | null) {
  if (from === to) return;
  const step = (characterId: string, transferToVault: boolean) => bungie('/Destiny2/Actions/Items/TransferItem/', {
    token, method: 'POST',
    body: { itemReferenceHash: i.hash, stackSize: i.quantity, transferToVault, itemId: i.instanceId || '0', characterId, membershipType: player.membershipType },
  });
  if (from) await step(from, true);
  if (to) await step(to, false);
}

// ---------------- the game's loadouts ----------------

export interface Loadout { index: number; name: string; icon: string; color: string; items: number; ids: string[] }   // ids: the items' instances (subclass included)

export async function loadLoadouts(player: Player, token: string, characterId: string): Promise<Loadout[]> {
  const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/?components=206`, { token });
  const all: any[] | undefined = p.characterLoadouts?.data?.[characterId]?.loadouts;
  if (!all) throw new BungieError(0, 'Private', "Bungie did not include this character's loadouts. Sign in with the Bungie account that owns it.");
  const t = await tablesOf('DestinyLoadoutNameDefinition', 'DestinyLoadoutIconDefinition', 'DestinyLoadoutColorDefinition');
  return all.map((l, index) => ({
    index,
    name: t.DestinyLoadoutNameDefinition[l.nameHash]?.name || `Loadout ${index + 1}`,
    icon: t.DestinyLoadoutIconDefinition[l.iconHash]?.iconImagePath ?? '',
    color: t.DestinyLoadoutColorDefinition[l.colorHash]?.colorImagePath ?? '',
    ids: (l.items ?? []).map((it: any) => it.itemInstanceId ?? '').filter((id: string) => id.length > 1),
    items: (l.items ?? []).filter((it: any) => (it.itemInstanceId ?? '').length > 1).length,
  }));   // empty ones too (the Item Manager shows every slot; its menu leaves them out)
}

export async function equipLoadout(player: Player, token: string, characterId: string, index: number) {
  await bungie('/Destiny2/Actions/Loadouts/EquipLoadout/', { token, method: 'POST', body: { loadoutIndex: index, characterId, membershipType: player.membershipType } });
}

// ---------------- vendors ----------------

export interface VendorSale { hash: number; name: string; icon: string; typeName: string; itemType: number; quantity: number; cost: string; canBuy: boolean; owned: boolean; door?: number }
export interface VendorCategory { name: string; sales: VendorSale[] }
export interface Vendor { hash: number; name: string; subtitle: string; desc: string; icon: string; rank?: number; refresh?: Date; categories: VendorCategory[] }
export const saleCount = (v: Vendor) => v.categories.reduce((s, c) => s + c.sales.length, 0);
export const isTenet = (v: Vendor) => /^Tenet of/i.test(v.name);

const VENDOR_ORDER = ['Zavala', 'Shaxx', 'Drifter', 'Nimbus', 'Banshee', 'Ada-1', 'Rahool', 'Tess', 'Hawthorne', 'Xûr', 'Starhorse', 'Saint-14', 'Saladin', 'Fynch', 'Devrim', 'Failsafe', 'Variks', 'Eris', 'Shaw Han'];
const noVars = (s: string) => s.replace(/\s*\([^)]*\{var:[^)]*\)|\{var:\d+\}/g, '').trim();

export async function loadVendors(player: Player, token: string, characterId: string): Promise<Vendor[]> {
  const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/Character/${characterId}/Vendors/?components=400,401,402`, { token });
  const vendors: Record<string, any> | undefined = p.vendors?.data;
  if (!vendors) throw new BungieError(0, 'Private', 'Bungie did not include the vendors. Sign in with the Bungie account that owns this character.');
  return buildVendors(p);
}

export const XUR = 2190858386;

export { xurWindow } from '../lib/schedule';

/**
 * Xûr this week, from Bungie's public vendor list (no sign-in: the same shelves for everyone, without the player's own
 * "owned" marks): undefined when he is away. With his next refresh, when his stock changes.
 */
export async function loadXur(): Promise<Vendor | undefined> {
  const p = await bungie<any>('/Destiny2/Vendors/?components=400,401,402');
  return (await buildVendors(p, true)).find((v) => v.hash === XUR);
}

async function buildVendors(p: any, everyone = false): Promise<Vendor[]> {
  const vendors: Record<string, any> = p.vendors?.data ?? {};
  const [vendorDefs, lite] = await Promise.all([table<any>('DestinyVendorDefinition'), items()]);
  const sales = p.sales?.data ?? {};
  const list: Vendor[] = [];
  const opens = new Map<string, number>();   // a sale that is really a doorway to another vendor's shelf
  // the preview vendor of an item is only in the full item definitions
  const doorways = new Map<number, number>();
  await Promise.all(Object.values<any>(sales).flatMap((s) => Object.values<any>(s.saleItems ?? {}))
    // the doorways seen (2026-10-17: Attunements, Legacy Gear, Fragments, Movement...) are untyped items (item type 0 or 20,
    // no type name); checked on the owner's vendors: this catches all 136, where a test on the type name caught none
    .filter((s) => { const d = lite[s.itemHash]; return !!d && ((d.itemType === 0 || d.itemType === 20) && !d.type || d.itemType === 0 && /Preview|Armor|Collection/i.test(d.type)); })
    .map(async (s) => { const d = await definition<any>('DestinyInventoryItemDefinition', s.itemHash); if (d?.preview?.previewVendorHash) doorways.set(s.itemHash, d.preview.previewVendorHash); }));
  for (const [vh, live] of Object.entries<any>(vendors)) {
    if (live.enabled === false) continue;
    const def = vendorDefs[vh];
    const name = noVars(def?.displayProperties?.name ?? '');
    if (!name) continue;
    const shown: any[] = def?.displayCategories ?? [];
    const onSale = sales[vh]?.saleItems ?? {};
    const cats: VendorCategory[] = [];
    for (const c of p.categories?.data?.[vh]?.categories ?? []) {
      const title = noVars(shown[c.displayCategoryIndex]?.displayProperties?.name ?? '');
      const offers: VendorSale[] = [];
      for (const ix of c.itemIndexes ?? []) {
        const s = onSale[ix];
        const d = s && lite[s.itemHash];
        if (!d?.name) continue;
        const cost = (s.costs ?? []).map((k: any) => ({ n: lite[k.itemHash]?.name ?? '', q: k.quantity ?? 0 })).filter((k: any) => k.n && k.q > 0)
          .map((k: any) => `${k.q.toLocaleString()} ${k.n}`).join(', ');
        const door = doorways.get(s.itemHash);
        if (door && vendors[door]) opens.set(`${vh}|${s.itemHash}`, door);
        offers.push({ hash: s.itemHash, name: d.name, icon: d.icon, typeName: d.type, itemType: d.itemType, quantity: Math.max(1, s.quantity ?? 1), cost, canBuy: (s.saleStatus ?? 0) === 0, owned: ((s.saleStatus ?? 0) & (128 | 4096)) !== 0, door });
      }
      if (offers.length) cats.push({ name: title || 'For sale', sales: offers });
    }
    if (!cats.length) continue;
    const dp = def?.displayProperties ?? {};
    const next = live.nextRefreshDate ? new Date(live.nextRefreshDate) : undefined;
    list.push({
      hash: Number(vh), name, subtitle: dp.subtitle ?? '', desc: dp.description ?? '', icon: dp.smallTransparentIcon || dp.icon || '',
      rank: live.progression?.level, refresh: next && next.getFullYear() < 3000 ? next : undefined, categories: cats,
    });
  }
  // the people you can visit, in the Tower's order; the shelves behind their doorway items laid out under them
  const byHash = new Map(list.map((v) => [v.hash, v]));
  const behind = new Set(opens.values());
  const shelves = (v: Vendor, depth: number): VendorCategory[] => {
    const result: VendorCategory[] = [], after: VendorCategory[] = [];
    for (const c of v.categories) {
      const plain = c.sales.filter((s) => !opens.has(`${v.hash}|${s.hash}`));
      if (plain.length) result.push({ ...c, sales: plain });
      if (depth >= 2) continue;
      for (const s of c.sales) {
        const door = opens.get(`${v.hash}|${s.hash}`);
        const sub = door ? byHash.get(door) : undefined;
        if (!sub) continue;
        const inside = shelves(sub, depth + 1).flatMap((x) => x.sales);
        if (inside.length) after.push({ name: sub.name, sales: inside });
      }
    }
    return [...after, ...result];
  };
  const rank = (v: Vendor) => { const at = VENDOR_ORDER.findIndex((o) => v.name.toLowerCase().includes(o.toLowerCase())); return at >= 0 ? at : 999; };
  return list.filter((v) => (everyone || v.subtitle || isTenet(v)) && !behind.has(v.hash)).map((v) => ({ ...v, categories: shelves(v, 0) })).filter((v) => saleCount(v) > 0)
    .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

// ---------------- quests, bounties and orders ----------------

export interface QuestObjective { text: string; progress: number; goal: number; done: boolean }
export interface QuestItem {
  hash: number; name: string; desc: string; icon: string; bounty: boolean; typeName: string; questLine: string;
  objectives: QuestObjective[]; expires?: Date; tracked: boolean; exotic: boolean; order: boolean;
}
export const questDone = (q: QuestItem) => q.objectives.length > 0 && q.objectives.every((o) => o.done);
export const questFraction = (q: QuestItem) => q.objectives.length === 0 ? 0
  : q.objectives.reduce((s, o) => s + (o.done ? 1 : o.goal > 0 ? Math.min(1, o.progress / o.goal) : 0), 0) / q.objectives.length;

const QUEST_BUCKET = 1345459588, ORDERS_BUCKET = 635141261;

export async function loadQuests(player: Player, token: string, characterId: string): Promise<QuestItem[]> {
  const p = await bungie<any>(`/Destiny2/${player.membershipType}/Profile/${player.membershipId}/?components=201,301`, { token });
  const carried: any[] | undefined = p.characterInventories?.data?.[characterId]?.items;
  if (!carried) throw new BungieError(0, 'Private', "Bungie did not include this character's inventory. Sign in with the Bungie account that owns it.");
  const [lite, objectives] = await Promise.all([items(), table<any>('DestinyObjectiveDefinition')]);
  const instanced = p.itemComponents?.objectives?.data ?? {};
  const loose = p.characterUninstancedItemComponents?.[characterId]?.objectives?.data ?? {};
  const list: QuestItem[] = [];
  const mine = carried.filter((i) => i.bucketHash === QUEST_BUCKET || i.bucketHash === ORDERS_BUCKET);
  // the quest line's name is only in the full definitions
  const lines = new Map<number, string>();
  await Promise.all([...new Set(mine.map((i) => i.itemHash))].map(async (h) => {
    const d = await definition<any>('DestinyInventoryItemDefinition', h);
    if (d?.setData?.questLineName) lines.set(h, d.setData.questLineName);
  }));
  for (const i of mine) {
    const d = lite[i.itemHash];
    if (!d?.name) continue;
    const objs: any[] = (i.itemInstanceId ? instanced[i.itemInstanceId]?.objectives : undefined) ?? loose[i.itemHash]?.objectives ?? [];
    list.push({
      hash: i.itemHash, name: d.name, desc: d.desc, icon: d.icon, bounty: d.itemType === 26, typeName: d.type, questLine: lines.get(i.itemHash) ?? '',
      objectives: objs.map((o) => ({ text: objectives[o.objectiveHash]?.progressDescription ?? '', progress: o.progress ?? 0, goal: o.completionValue ?? 0, done: !!o.complete })),
      expires: i.expirationDate ? new Date(i.expirationDate) : undefined, tracked: ((i.state ?? 0) & 2) !== 0, exotic: d.tier === 6, order: i.bucketHash === ORDERS_BUCKET,
    });
  }
  return list.sort((a, b) => Number(b.tracked) - Number(a.tracked) || questFraction(b) - questFraction(a) || a.name.localeCompare(b.name));
}
